import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ChevronLeft,
  FolderPlus,
  ImagePlus,
  FolderDown,
  Play,
  Link2,
  HardDrive,
  Images,
  Pencil,
  Sparkles,
  ShieldCheck,
} from 'lucide-react';
import clsx from 'clsx';

import { useApi } from '../api-context';
import { Spinner } from '../components/Primitives';
import ShootPreview from '../components/ShootPreview';
import ExportDialog from '../components/ExportDialog';
import ReviewAssistCard from '../components/ReviewAssistCard';
import ReviewAssistPrompt from '../components/ReviewAssistPrompt';
import { useReviewAssist } from '../hooks/useReviewAssist';
import type {
  Bucket,
  MediaItem,
  Project as ProjectType,
  ScanProgress,
  SourceWithCount,
} from '../../types';
import type { SourceAvailability } from '../../api';

const PREVIEW_COUNT = 12;

export default function Project() {
  const api = useApi();
  const navigate = useNavigate();
  const { projectId: raw } = useParams();
  const projectId = Number(raw);

  const [project, setProject] = useState<ProjectType | null>(null);
  const [buckets, setBuckets] = useState<Bucket[]>([]);
  const [sources, setSources] = useState<SourceWithCount[]>([]);
  const [health, setHealth] = useState<Record<number, SourceAvailability>>({});
  const [prompts, setPrompts] = useState<Record<number, number>>({});
  const [cover, setCover] = useState<MediaItem[]>([]);
  const [picks, setPicks] = useState<MediaItem[]>([]);
  const [pickIds, setPickIds] = useState<ReadonlySet<number>>(() => new Set());
  const [picksOnly, setPicksOnly] = useState(false);
  const [scan, setScan] = useState<ScanProgress | null>(null);
  const [showExport, setShowExport] = useState(false);
  const assist = useReviewAssist(projectId);
  const [askAssist, setAskAssist] = useState(false);
  const [busy, setBusy] = useState(false);

  /**
   * Re-read what the review produced.
   *
   * Its own function because it has to run at the end of a review as well as on
   * arrival, and re-reading the sources at that moment would mean a round of
   * permission checks nobody asked for.
   */
  const refreshPicks = useCallback(() => {
    // The suggestions, so the shoot page can show what a review actually
    // produced rather than making the photographer open the culling screen to
    // find out.
    const marked = api.assistMarkers(projectId);
    const ids = new Set([...marked].filter(([, m]) => m.isPick).map(([id]) => id));
    setPickIds(ids);
    setPicks(api.getMediaByIds([...ids]).slice(0, PREVIEW_COUNT));
    setProject(api.getProject(projectId));
  }, [api, projectId]);

  const refresh = useCallback(async () => {
    const found = api.getProject(projectId);
    if (!found) {
      navigate('/', { replace: true });
      return;
    }
    setProject(found);
    setBuckets(api.listBuckets(projectId));
    setCover(api.listMedia(projectId, { limit: PREVIEW_COUNT, offset: 0, filter: 'all' }).items);
    refreshPicks();

    const list = api.listSources(projectId);
    setSources(list);

    const states: Record<number, SourceAvailability> = {};
    const costs: Record<number, number> = {};
    for (const source of list) {
      states[source.id] = await api.sourceAvailability(source.id);
      if (states[source.id] !== 'ready') costs[source.id] = await api.promptCost(source.id);
    }
    setHealth(states);
    setPrompts(costs);
  }, [api, projectId, navigate, refreshPicks]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  /**
   * Pick up the result the moment a review lands.
   *
   * The card above already followed the run, so the page would announce a
   * finished review and then show none of it: no badges on the thumbnails, no
   * All / AI picks toggle, until the photographer reloaded by hand. Subscribing
   * to the terminal event rather than reading `progress` means a second review
   * that also ends 'done' still refreshes, where comparing state would not.
   */
  useEffect(
    () =>
      api.onAssistProgress(projectId, (update) => {
        if (update.phase === 'done' || update.phase === 'failed') refreshPicks();
      }),
    [api, projectId, refreshPicks],
  );

  // Offering to start before the folders are reattached means burning through
  // every frame reading nothing. The sources are resolved asynchronously on
  // load, so this is a real window, not a theory.
  const ready = sources.length > 0 && sources.every((s) => health[s.id] === 'ready');

  /**
   * Whether to review a shoot is a question about the shoot, not about the
   * button that filled it — so it is asked here, once, on arrival.
   *
   * It used to be asked at import instead. But Home and Shoots both create a
   * shoot and navigate straight to this page without going through the import
   * handler below, so the primary path never asked at all, and the standing
   * "do this for every shoot" preference was dead along with it.
   */
  const asked = useRef(false);
  useEffect(() => {
    asked.current = false;
  }, [projectId]);

  const offerAssist = useCallback(() => {
    if (asked.current) return;
    asked.current = true;

    const decision = api.assistDecision(projectId);
    if (decision === 'declined') return;
    if (decision === 'deep') assist.start();
    else setAskAssist(true);
    // `assist.start` is stable; the object around it is not.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, projectId, assist.start]);

  useEffect(() => {
    if (!project || project.itemCount === 0 || !ready) return;
    // A review already running, or already finished, has answered the question.
    if (assist.running || assist.isDeep) {
      asked.current = true;
      return;
    }
    offerAssist();
  }, [project, ready, assist.running, assist.isDeep, offerAssist]);

  const add = async (run: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await run();
      await refresh();

      // Newly added frames are unscored whatever this shoot already decided, so
      // a shoot that said yes gets a fresh pass over them rather than waiting
      // for the next visit.
      if (api.assistDecision(projectId) === 'deep') assist.start();
      else offerAssist();
    } finally {
      setBusy(false);
      setScan(null);
    }
  };

  const addFolder = () => add(() => api.addFolder(projectId, setScan));
  const addFiles = () => add(() => api.addFiles(projectId, setScan));

  const restore = async (sourceId: number) => {
    const ok = await api.requestSourceAccess(sourceId);
    if (!ok) await api.relinkSource(sourceId);
    await refresh();
  };

  const rename = () => {
    const name = prompt('Rename this shoot', project?.name);
    if (name) {
      api.renameProject(projectId, name);
      void refresh();
    }
  };

  if (!project) {
    return (
      <div className="flex h-full items-center justify-center text-ink-600">
        <Spinner className="h-5 w-5" />
      </div>
    );
  }

  const left = project.itemCount - project.reviewedCount;
  const done = project.itemCount > 0 ? project.reviewedCount / project.itemCount : 0;
  // Carrying the lens through means "show me the picks" is one decision, not
  // two: the culling screen opens already narrowed to the same frames.
  const review = () => navigate(`/p/${projectId}/review${picksOnly ? '?lens=picks' : ''}`);

  return (
    <div className="flex min-h-full flex-col">
      {/* ---------------- app header ---------------- */}
      <header className="sticky top-0 z-30 border-b border-ink-850 bg-ink-950/85 backdrop-blur-xl">
        <div className="mx-auto flex h-14 w-full max-w-content items-center gap-4 px-5 sm:px-8">
          <button
            className="flex items-center gap-1.5 text-caption text-ink-400 transition hover:text-ink-50"
            // The library, not the landing page: the link says "All shoots"
            // and should land where the shoots are.
            onClick={() => navigate('/shoots')}
          >
            <ChevronLeft size={16} />
            All shoots
          </button>
          <button className="ml-auto" onClick={() => navigate('/')} aria-label="m3cull home">
            <img
              src={`${import.meta.env.BASE_URL}brand/lockup.png`}
              alt="m3cull"
              width={394}
              height={96}
              className="h-5 w-auto opacity-80 transition hover:opacity-100"
            />
          </button>
        </div>
      </header>

      <main id="main" className="mx-auto w-full max-w-content flex-1 px-5 pb-24 pt-10 sm:px-8 sm:pt-14">
        {/* ---------------- title ---------------- */}
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div className="min-w-0">
            <p className="label">Shoot</p>
            <h1 className="mt-2.5 truncate text-section font-semibold tracking-tight text-ink-50">
              {project.name}
            </h1>
            {/* Plain text flow, not flex — a flex gap would space the words apart too. */}
            <p className="tnum mt-3 text-body text-ink-400">
              <span className="text-ink-200">{project.itemCount}</span> photos
              <span className="px-2 text-ink-600" aria-hidden="true">·</span>
              {left > 0 ? (
                <>
                  <span className="text-ink-200">{left}</span> still to review
                </>
              ) : (
                <span className="text-keep">all reviewed</span>
              )}
            </p>
          </div>

          <div className="flex shrink-0 flex-wrap items-center gap-2.5">
            <button className="btn-ghost" onClick={addFolder} disabled={busy}>
              <FolderPlus size={14} />
              Add folder
            </button>
            <button className="btn-ghost" onClick={rename}>
              <Pencil size={14} />
              Rename
            </button>

            {/*
              One loud button, and which one it is depends on the shoot.
              Two primaries side by side is not a promotion, it is a fork: the
              page stops having an obvious next step. Before a review has run,
              the review is the next step. After it, the picks are waiting and
              culling is. So they swap rather than compete.
            */}
            {assist.isDeep || assist.running ? (
              <>
                {!assist.running && (
                  // Names the AI outright. "Review again" read as re-doing the
                  // manual cull, which is what the button beside it used to say,
                  // and a ghost button next to a filled one reads as a link.
                  <button className="btn-assist px-4 py-2.5 text-body" onClick={assist.start}>
                    <Sparkles size={15} />
                    Ask AI again
                  </button>
                )}
                <button
                  className="btn-primary px-5 py-2.5 text-body"
                  onClick={review}
                  disabled={project.itemCount === 0}
                >
                  <Play size={15} />
                  {left > 0 ? 'Continue culling' : 'Open shoot'}
                </button>
              </>
            ) : (
              <>
                <button
                  className="btn-ghost px-4 py-2.5 text-body"
                  onClick={review}
                  disabled={project.itemCount === 0}
                >
                  <Play size={15} />
                  {left > 0 ? 'Continue culling' : 'Open shoot'}
                </button>
                <button
                  className="btn-primary px-5 py-2.5 text-body"
                  onClick={assist.start}
                  disabled={project.itemCount === 0 || !ready}
                >
                  <Sparkles size={15} />
                  Find my best shots
                </button>
              </>
            )}
          </div>
        </div>

        {/*
          The sentence that makes the feature safe to say yes to, kept next to
          the button that asks for the yes. Buried in a card three screens down
          it did no work at all — and it is the one claim no competitor running
          in the cloud can make.
        */}
        {project.itemCount > 0 && !assist.isDeep && !assist.running && (
          <p className="mt-4 flex items-center gap-2 text-caption text-ink-500 lg:justify-end">
            <ShieldCheck size={14} className="shrink-0 text-keep" />
            {ready
              ? 'Groups your bursts and marks the sharpest of each. Runs on your own machine. Your photos never leave it.'
              : 'Reconnect this shoot’s folder first. Review Assist reads the photos to work.'}
          </p>
        )}

        {/* ---------------- progress ---------------- */}
        <div className="mt-8 flex items-center gap-4">
          <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-ink-850">
            <div
              className="h-full rounded-full bg-ink-100 transition-[width] duration-500 ease-out"
              style={{ width: `${done * 100}%` }}
            />
          </div>
          <span className="tnum shrink-0 text-caption text-ink-400">
            {Math.round(done * 100)}%
          </span>
        </div>

        {/* Reports on the review, directly under the action that starts one. */}
        {project.itemCount > 0 && (
          <ReviewAssistCard
            progress={assist.progress}
            running={assist.running}
            isDeep={assist.isDeep}
            onStop={assist.stop}
          />
        )}

        {/* ---------------- the shoot itself ---------------- */}
        <section className="mt-10">
          {pickIds.size > 0 && (
            <div className="mb-3 flex items-center gap-1 rounded-lg border border-ink-800 p-0.5 w-fit">
              <button
                onClick={() => setPicksOnly(false)}
                className={clsx(
                  'rounded-md px-3 py-1.5 text-caption transition',
                  picksOnly ? 'text-ink-400 hover:text-ink-100' : 'bg-ink-800 text-ink-50',
                )}
              >
                All <span className="tnum text-ink-500">{project.itemCount}</span>
              </button>
              <button
                onClick={() => setPicksOnly(true)}
                className={clsx(
                  'flex items-center gap-1.5 rounded-md px-3 py-1.5 text-caption transition',
                  picksOnly ? 'bg-pick-soft text-pick' : 'text-ink-400 hover:text-ink-100',
                )}
              >
                <span className="h-1.5 w-1.5 rounded-full bg-pick" />
                AI picks <span className="tnum text-ink-500">{pickIds.size}</span>
              </button>
            </div>
          )}

          <ShootPreview
            items={picksOnly ? picks : cover}
            buckets={buckets}
            total={picksOnly ? pickIds.size : project.itemCount}
            picks={pickIds}
            onOpen={review}
          />
        </section>

        {/* ---------------- counts ---------------- */}
        <dl className="mt-10 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Stat label="Photos" value={project.itemCount} />
          {buckets.map((bucket) => (
            <Stat key={bucket.id} label={bucket.name} value={bucket.count} tone={bucket.kind} />
          ))}
          <Stat label="Unreviewed" value={left} />
        </dl>


        {/* ---------------- sources ---------------- */}
        <section className="mt-10">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-ink-850 pb-3">
            <h2 className="label">Sources</h2>
            <div className="flex gap-1">
              <button className="btn-ghost" onClick={addFolder} disabled={busy}>
                <FolderPlus size={14} /> Add folder
              </button>
              <button className="btn-ghost" onClick={addFiles} disabled={busy}>
                <ImagePlus size={14} /> Add photos
              </button>
            </div>
          </div>

          {scan && (
            <div className="mt-4 flex items-center gap-3 text-caption text-ink-400">
              <Spinner className="h-4 w-4" />
              <span>
                {scan.sourceName}: {scan.scanned} found
              </span>
            </div>
          )}

          <ul className="divide-y divide-ink-850">
            {sources.map((source) => {
              const state = health[source.id];
              return (
                <li key={source.id} className="flex items-center gap-3.5 py-4">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-ink-800 text-ink-500">
                    {isPicked(source.name) ? <Images size={15} /> : <HardDrive size={15} />}
                  </span>

                  <div className="min-w-0 flex-1">
                    <p className="truncate text-body text-ink-100">{source.name}</p>
                    <p className="tnum mt-0.5 text-caption text-ink-500">
                      {source.itemCount} photos
                      {state !== 'ready' && (prompts[source.id] ?? 0) > 1 && (
                        <> · {prompts[source.id]} permission prompts to reconnect</>
                      )}
                    </p>
                  </div>

                  {state === 'ready' && (
                    <span className="flex shrink-0 items-center gap-1.5 text-caption text-ink-500">
                      <span className="h-1.5 w-1.5 rounded-full bg-keep" aria-hidden="true" />
                      Connected
                    </span>
                  )}
                  {state !== 'ready' && state !== undefined && (
                    <button className="btn-outline shrink-0 py-1.5" onClick={() => restore(source.id)}>
                      <Link2 size={13} />
                      Reconnect
                    </button>
                  )}
                </li>
              );
            })}
          </ul>

          {sources.some((s) => health[s.id] && health[s.id] !== 'ready') && (
            <p className="mt-4 max-w-2xl text-caption leading-relaxed text-ink-500">
              Browsers forget file access between visits. Your culling decisions are already saved, and
              reconnecting only restores the photos. A folder takes one click; hand-picked photos
              ask once per file, so a folder is worth preferring for large sets.
            </p>
          )}
        </section>
        {/* ---------------- export ---------------- */}
        <section className="mt-10 flex flex-col gap-4 rounded-xl border border-ink-800 bg-ink-900 p-6 sm:flex-row sm:items-center sm:justify-between sm:p-7">
          <div>
            <h2 className="text-head font-semibold text-ink-50">Export your keepers</h2>
            <p className="mt-1.5 max-w-md text-caption leading-relaxed text-ink-400">
              {project.reviewedCount === 0
                ? 'Nothing to export yet. Mark a few photos as keep or reject first.'
                : 'Copies go into a folder you choose. Your originals are never modified.'}
            </p>
          </div>

          <button
            className="btn-primary shrink-0 px-5 py-2.5 text-body"
            onClick={() => setShowExport(true)}
            disabled={project.reviewedCount === 0}
          >
            <FolderDown size={15} />
            Export
          </button>
        </section>

      </main>

      {askAssist && (
        <ReviewAssistPrompt
          photoCount={project.itemCount}
          onAnswer={(choice, always) => {
            setAskAssist(false);
            assist.answer(choice, always);
          }}
        />
      )}

      {showExport && (
        <ExportDialog
          projectId={projectId}
          buckets={buckets}
          onClose={() => {
            setShowExport(false);
            void refresh();
          }}
        />
      )}
    </div>
  );
}

/** Sources created by picking files are named by the facade, not by a folder. */
function isPicked(name: string): boolean {
  return /^Selected photos( \(\d+\))?$/.test(name);
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone?: 'keep' | 'reject';
}) {
  return (
    <div className="flex flex-col gap-1.5 rounded-xl border border-ink-800 bg-ink-900 px-5 py-4">
      <dt className="label">{label}</dt>
      <dd
        className={clsx(
          'tnum text-title font-semibold',
          tone === 'keep' && 'text-keep',
          tone === 'reject' && 'text-reject',
          !tone && 'text-ink-50',
        )}
      >
        {value}
      </dd>
    </div>
  );
}
